import { useCallback, useEffect, useState } from "react";

import { AccountCircleIcon } from "@/components/Icons/AccountCircleIcon";
import { HomeIcon } from "@/components/Icons/HomeIcon";
import { FindMeResponse } from "@/types/api/user";
import { MenuOption } from "@/types/pages/dashboard";
import { findMe } from "@/services/user";
import { getMenuOptions } from "@/features/dashboard/utils/getMenuOptions";
import { useLocation, useNavigate } from "react-router-dom";

interface UseDashboardLayoutResponse {
  selectedMenu: MenuOption | undefined;
  isMenuOpened: boolean;
  changeIsMenuOpened: (option?: MenuOption) => void;
  selectedMenuOption: string;
  changeSelectedMenuOption: (label: string) => void;
  selectedSubmenuOption: string;
  changeSelectedSubmenuOption: (label: string) => void;
  menuOptions: MenuOption[];
  userInfo: FindMeResponse | undefined;
}

export const useDashboardLayout = (): UseDashboardLayoutResponse => {
  const location = useLocation();
  const pathname = location.pathname;

  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [selectedMenuOption, setSelectedMenuOption] = useState("Início");
  const [selectedSubmenuOption, setSelectedSubenuOption] = useState("Meus Planos");
  const [userInfo, setUserInfo] = useState<FindMeResponse>();
  const [menuOptions, setMenuOptions] = useState<MenuOption[]>([]);

  const selectedMenu = menuOptions.find((o) => o.label === selectedMenuOption);

  const changeIsMenuOpened = useCallback(
    (option?: MenuOption) => {
      if (!option && !selectedMenu?.subOptions) {
        setIsMenuOpened(false);
        return;
      }

      if (option && option.subOptions) {
        setIsMenuOpened(true);
        return;
      }

      if (option && !option.subOptions) {
        setIsMenuOpened(false);
        return;
      }

      setIsMenuOpened((prev) => !prev);
    },
    [selectedMenu],
  );

  const findMenuOptions = async () => {
    const defaultMenuOptions: MenuOption[] = [
      {
        icon: <HomeIcon color="#fff" />,
        label: "Início",
        link: "/dash/home",
      },
      {
        icon: <AccountCircleIcon color="#fff" />,
        label: "Minha Conta",
        subOptionsTitle: "Minha Conta",
        subOptions: [
          {
            label: "Dados da conta",
            link: "/dash/my-account",
          },
          {
            label: "Meus Planos",
            link: "/dash/my-plans",
          },
        ],
      },
    ];

    try {
      const res = await findMe();

      setUserInfo(res);

      const newMenuOptions = getMenuOptions(res.permissions);

      defaultMenuOptions.push(...newMenuOptions);
    } catch (err) {
      console.log(err);
    }

    setMenuOptions(defaultMenuOptions);
  };

  useEffect(() => {
    findMenuOptions();
  }, []);

  useEffect(() => {
    if (!pathname || menuOptions.length === 0) {
      return;
    }

    for (const option of menuOptions) {
      if (option.link === pathname) {
        setSelectedMenuOption(option.label);
        // Option has no subOptions (it's a direct link) — keep the
        // secondary panel closed instead of leaving it pinned open+empty.
        setIsMenuOpened(false);
        return;
      }

      const matchingSubOption = option.subOptions?.find(
        (subOption) => subOption.link === pathname,
      );

      if (matchingSubOption) {
        setSelectedMenuOption(option.label);
        setSelectedSubenuOption(matchingSubOption.label);
        setIsMenuOpened(true);
        return;
      }
    }
  }, [pathname, menuOptions]);

  return {
    selectedMenu,
    isMenuOpened,
    changeIsMenuOpened,
    selectedMenuOption,
    changeSelectedMenuOption: (label) => setSelectedMenuOption(label),
    selectedSubmenuOption,
    changeSelectedSubmenuOption: (label) => setSelectedSubenuOption(label),
    menuOptions,
    userInfo,
  };
};
